import { boxedLabel } from "./boxedLabelConfiguration.js";
import {
	GRAPH_DEFAULTS,
	GRAPH_MIRROR_DIRECTIONS,
	LABEL_VALUES,
	MOTIVES_TYPES,
	SCALES,
} from "../Enums/index.js";
import { formatNumberForBar } from "../Scales/scaleNumberFormatter.js";
import isAtTick from "./isAtTick.js";
import DocumentDO = App.Data.Explorer.DocumentDO;

export function getLabelRect({ x, y, width, height = 12, padding = 0 }) {
	return {
		left: x - width / 2 - padding,
		right: x + width / 2 + padding,
		top: y - height - padding,
		bottom: y + padding,
	};
}

export function overlaps(a, b, padding = 0) {
	const aLeft = a.left - padding;
	const aRight = a.right + padding;
	const aTop = a.top - padding;
	const aBottom = a.bottom + padding;

	const bLeft = b.left - padding;
	const bRight = b.right + padding;
	const bTop = b.top - padding;
	const bBottom = b.bottom + padding;

	return aLeft < bRight && aRight > bLeft && aTop < bBottom && aBottom > bTop;
}

export const totalAmountLabel = (
	derivedDocument: DocumentDO,
	isDarkMode: boolean,
	mirrorDirection = GRAPH_MIRROR_DIRECTIONS.UP
) => {
	return {
		formatter: (value, ctx) => {
			const meta = ctx.chart.getDatasetMeta(ctx.datasetIndex);
			const model = meta.data[ctx.dataIndex];
			const { width } = model.getProps(["width"], true);

			let label;
			const totalLabelValue = derivedDocument.is_visualised_as_percentage_share
				? LABEL_VALUES.PERCENTAGE
				: derivedDocument.total_label_value;

			const totalStackValue =
				ctx.chart.$totalizer.stacktotal[
					ctx.dataset.yAxisID === SCALES.Y_SECONDARY
						? SCALES.Y_SECONDARY
						: SCALES.Y
				][`${ctx.dataset.stack}-${mirrorDirection}-${ctx.dataset.motivesType}`][
					ctx.dataIndex
				];
			switch (totalLabelValue) {
				case LABEL_VALUES.PERCENTAGE: {
					if (!derivedDocument.visible_capacity_types?.length) {
						label = "100%";
						break;
					}
					const barTotal =
						ctx.chart.$totalizer.stacktotal[SCALES.Y][
							`${ctx.dataset.stack}-${ctx.dataset.mirrorDirection}-capacity`
						][ctx.dataIndex];

					label = formatNumberForBar(
						"percentage",
						derivedDocument.label_scale_format,
						totalStackValue / barTotal
					);
					break;
				}
				case LABEL_VALUES.PERCENTAGE_OF_TOTAL: {
					let total = 0;
					if (
						ctx.dataset.yAxisID === SCALES.Y ||
						ctx.dataset.yAxisID === SCALES.Y_CAPACITY
					) {
						total =
							ctx.chart.$totalizer[
								`graphYTotal_${ctx.dataset.mirrorDirection}`
							] + ctx.chart.$totalizer[`graphYCapacityTotal`];
					}
					if (ctx.dataset.yAxisID === SCALES.Y_SECONDARY) {
						total = ctx.chart.$totalizer.graphYSecondaryTotal;
					}
					if (ctx.dataset.yAxisID === SCALES.Y_MIN) {
						total = ctx.chart.$totalizer.graphYMinTotal;
					}
					if (ctx.dataset.yAxisID === SCALES.Y_MAX) {
						total = ctx.chart.$totalizer.graphYMaxTotal;
					}
					// if (ctx.dataset.yAxisID === SCALES.Y_CAPACITY) {
					// 	total = Math.round(ctx.chart.$totalizer.graphYCapacityTotal);
					// }
					label = formatNumberForBar(
						"percentage",
						derivedDocument.label_scale_format,
						totalStackValue / total,
						width
					);
					break;
				}
				default: {
					// 'amount-label' is default
					label = formatNumberForBar(
						derivedDocument.formatForGraph!,
						derivedDocument.label_scale_format,
						totalStackValue,
						width
					);
					break;
				}
			}

			const labelWidth =
				ctx.chart.$cachedWidths[label] ||
				(ctx.chart.$cachedWidths[label] =
					ctx.chart.ctx.measureText(label).width);

			const element = ctx.chart.getDatasetMeta(ctx.datasetIndex).data[
				ctx.dataIndex
			];
			const rect = getLabelRect({
				x: element.x,
				y: element.y,
				width: labelWidth,
			});

			const key = `${ctx.datasetIndex}:${ctx.dataIndex}`;
			const alreadyRecorded = ctx.chart.$totalLabelRects.has(key);

			if (!alreadyRecorded) {
				const overlapsOtherTotal = Array.from(
					ctx.chart.$totalLabelRects.values()
				).some((r) => overlaps(r.rect, rect, 3));

				if (overlapsOtherTotal) return null;

				ctx.chart.$totalLabelRects.set(key, {
					rect,
					label,
					datasetIndex: ctx.datasetIndex,
					dataIndex: ctx.dataIndex,
				});
			}
			return label;
		},
		align: (ctx) => {
			const value = ctx.dataset.data[ctx.dataIndex];
			return value >= 0 ? "end" : "start";
		},
		anchor: (ctx) => {
			const value = ctx.dataset.data[ctx.dataIndex];
			return value >= 0 ? "end" : "start";
		},
		...boxedLabel,
		font: function () {
			return {
				size: 12,
			};
		},
		color: function () {
			return isDarkMode
				? GRAPH_DEFAULTS.TEXT_COLOR_DARK
				: GRAPH_DEFAULTS.TEXT_COLOR;
		},
		display: function (ctx) {
			// Disable for secondary vertical.

			const isNotCapacityDataset =
				ctx.dataset.motivesType === MOTIVES_TYPES.BAR ||
				ctx.dataset.motivesType === MOTIVES_TYPES.LINE;
			if (
				ctx.dataset.yAxisID === SCALES.Y_SECONDARY ||
				(isNotCapacityDataset && derivedDocument.visible_capacity_types?.length)
			) {
				return false;
			}

			const utmost =
				ctx.chart.$totalizer.utmost[
					`${ctx.dataset.stack}-${mirrorDirection}-${ctx.dataset.motivesType}`
				] || [];

			return utmost[ctx.dataIndex] === ctx.datasetIndex &&
				isAtTick(ctx) &&
				derivedDocument.is_total_label_visible
				? "true"
				: false;
		},
	};
};
